import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';

// Traduce errores técnicos de edge functions a mensajes legibles en español.
function humanizeError(msg: string | null | undefined): string {
  if (!msg || typeof msg !== 'string') return 'Error desconocido.';
  if (msg.includes('GOOGLE_CALENDAR_API_KEY') || msg.includes('connect Google Calendar') || msg.includes('Google Calendar credentials'))
    return 'Google Calendar no está conectado. Verifica tus conectores en la configuración.';
  if (msg.includes('GOOGLE_DRIVE_API_KEY'))
    return 'Google Drive no está conectado. Verifica tus conectores en la configuración.';
  if (msg.includes('FIREFLIES_API_KEY') || msg.includes('connect Fireflies'))
    return 'Fireflies no está conectado. Verifica tus conectores en la configuración.';
  if (msg.includes('LOVABLE_API_KEY'))
    return 'La clave de API de Lovable no está configurada. Contacta al soporte.';
  if (msg.includes('Unauthorized') || msg.includes('Missing auth') || msg.includes('401'))
    return 'Sesión expirada. Recarga la página e inicia sesión nuevamente.';
  if (msg.includes('Forbidden') || msg.includes('403'))
    return 'No tienes permisos para realizar esta acción.';
  if (msg.includes('cohort_id required') || msg.includes('Cohort not found'))
    return 'La cohorte no existe o ya fue eliminada.';
  if (msg.includes('FetchError') || msg.includes('fetch failed') || msg.includes('Failed to fetch'))
    return 'Error de red. Verifica tu conexión a internet.';
  return msg;
}

export function useCohortCalendarSync() {
  const [syncingCalendar, setSyncingCalendar] = useState(false);
  const [syncingRecordings, setSyncingRecordings] = useState(false);
  const [syncingItemId, setSyncingItemId] = useState<string | null>(null);
  const qc = useQueryClient();

  const syncCalendar = async (cohortId: string, force = false, demoDayOnly = false) => {
    setSyncingCalendar(true);
    try {
      const { data, error } = await supabase.functions.invoke('sync-cohort-calendar', {
        body: { cohort_id: cohortId, force, demo_day_only: demoDayOnly },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const { created = 0, updated = 0, skipped = 0, errors = [] } = data || {};
      toast({
        title: demoDayOnly ? 'Demo Day sincronizado con Google Calendar' : 'Sincronizado con Google Calendar',
        description: demoDayOnly
          ? `El Demo Day se ${created ? 'creó' : 'actualizó'} en el calendario de los participantes (sin correo).${errors.length ? ` · Con errores: ${errors.length}` : ''}`
          : `Creados: ${created} · Actualizados: ${updated} · Omitidos: ${skipped}${errors.length ? ` · Con errores: ${errors.length}` : ''}`,
      });
      if (errors.length) console.warn('Calendar sync errors', errors);
      await qc.invalidateQueries({ queryKey: ['cohort-schedule-items'] });
      await qc.invalidateQueries({ queryKey: ['cohort-management', 'schedule'] });
      return data;
    } catch (e: any) {
      // Las respuestas no-2xx llegan como error genérico: leer el cuerpo real.
      let msg = e?.message as string | undefined;
      try {
        const ctx = e?.context;
        if (ctx && typeof ctx.text === 'function') {
          const body = await ctx.text();
          const parsed = JSON.parse(body);
          if (parsed?.error) msg = parsed.error;
        }
      } catch { /* usar e.message */ }
      toast({
        title: 'Error al sincronizar con Calendar',
        description: humanizeError(msg),
        variant: 'destructive',
      });
      throw e;
    } finally {
      setSyncingCalendar(false);
    }
  };

  // Sincroniza SOLO esa sesión puntual, sin recorrer el resto del cronograma
  // ni tocar el Demo Day — botón individual en cada tarjeta de sesión.
  const syncItem = async (cohortId: string, itemId: string) => {
    setSyncingItemId(itemId);
    try {
      const { data, error } = await supabase.functions.invoke('sync-cohort-calendar', {
        body: { cohort_id: cohortId, item_id: itemId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      const { created = 0, updated = 0, errors = [] } = data || {};
      toast({
        title: 'Sesión sincronizada con Google Calendar',
        description: `${created ? 'Evento creado' : updated ? 'Evento actualizado' : 'Sin cambios'}${errors.length ? ` · Con errores: ${errors.length}` : ''}`,
      });
      if (errors.length) console.warn('Calendar sync errors', errors);
      await qc.invalidateQueries({ queryKey: ['cohort-schedule-items'] });
      await qc.invalidateQueries({ queryKey: ['cohort-management', 'schedule'] });
      return data;
    } catch (e: any) {
      let msg = e?.message as string | undefined;
      try {
        const ctx = e?.context;
        if (ctx && typeof ctx.text === 'function') {
          const body = await ctx.text();
          const parsed = JSON.parse(body);
          if (parsed?.error) msg = parsed.error;
        }
      } catch { /* usar e.message */ }
      toast({
        title: 'Error al sincronizar la sesión',
        description: humanizeError(msg),
        variant: 'destructive',
      });
      throw e;
    } finally {
      setSyncingItemId(null);
    }
  };

  const syncRecordings = async (cohortId?: string) => {
    setSyncingRecordings(true);
    try {
      // Run all three sources in parallel — each is independent.
      // 1. fetch-calendar-recordings: Drive recordings from Calendar event attachments
      // 2. sync-fireflies-recordings: Fireflies transcripts + attendance
      // 3. sync-meet-attendance: Meet API v2 conferenceRecords (authoritative attendance)
      const [calResult, ffResult, meetAttResult] = await Promise.allSettled([
        supabase.functions.invoke('fetch-calendar-recordings', {
          body: cohortId ? { cohort_id: cohortId } : {},
        }),
        supabase.functions.invoke('sync-fireflies-recordings', {
          body: cohortId ? { cohort_id: cohortId } : {},
        }),
        supabase.functions.invoke('sync-meet-attendance', {
          body: cohortId ? { cohort_id: cohortId } : {},
        }),
      ]);

      // Extraer datos y errores de cada fuente por separado
      const calData     = calResult.status === 'fulfilled'      ? calResult.value.data      : null;
      const calErr      = calResult.status === 'fulfilled'
        ? (calResult.value.error || calData?.error || null)
        : calResult.reason;
      const ffData      = ffResult.status === 'fulfilled'       ? ffResult.value.data       : null;
      const ffErr       = ffResult.status === 'fulfilled'
        ? (ffResult.value.error || ffData?.error || null)
        : ffResult.reason;
      const meetAttData = meetAttResult.status === 'fulfilled'  ? meetAttResult.value.data  : null;

      if (calErr) console.warn('[syncRecordings] Meet recordings error:', calErr);
      if (ffErr)  console.warn('[syncRecordings] Fireflies error:', ffErr);

      const meetMatched = calData?.matched    || 0;
      const ffMatched   = ffData?.matched     || 0;
      const attSynced   = meetAttData?.synced || 0;

      const parts: string[] = [];
      if (calErr)         parts.push('Meet grabaciones: no disponible');
      else if (meetMatched > 0) parts.push(`${meetMatched} grabaciones Meet`);
      if (ffErr)          parts.push('Fireflies: no disponible');
      else if (ffMatched > 0)   parts.push(`${ffMatched} Fireflies`);
      if (attSynced > 0)        parts.push(`${attSynced} asistencias Meet`);
      if (!parts.length)        parts.push('Sin datos nuevos');

      toast({
        title: 'Grabaciones y asistencia sincronizadas',
        description: parts.join(' · '),
      });

      await qc.invalidateQueries({ queryKey: ['cohort-schedule-items'] });
      await qc.invalidateQueries({ queryKey: ['cohort-schedule'] });
      await qc.invalidateQueries({ queryKey: ['cohort-management', 'schedule'] });
      return { calData, ffData, meetAttData };
    } catch (e: any) {
      toast({
        title: 'Error al sincronizar grabaciones',
        description: humanizeError(e.message),
        variant: 'destructive',
      });
      throw e;
    } finally {
      setSyncingRecordings(false);
    }
  };

  return { syncCalendar, syncItem, syncRecordings, syncingCalendar, syncingItemId, syncingRecordings };
}
